/**
 * The contact record's client. CRM-BUILD item 31.
 *
 * ⚠️ SAME 401 REPORTING AS EVERY OTHER CALL in `inbox-api.ts`. Omitting it is
 * the failure recorded at `getJson`: a session that has ended and nothing
 * saying so.
 */
import { reportUnauthorized } from './unauthorized.js'
/** ⚠️ The server's own type, imported rather than restated: a second copy of a
 *  note's shape is a second place that must agree about what a note is. */
import type { RecordNote } from '../../../src/services/contact-record.js'

export type RecordField = {
  id: string
  label: string
  fieldType: string
  groupId: string
  retired: boolean
  hasValue: boolean
  value: string | null
  optionLabel: string | null
}

export type ContactRecordResponse = {
  id: string
  reference: number
  displayName: string | null
  firstName: string | null
  lastName: string | null
  email: string | null
  country: string | null
  countryName: string | null
  language: string | null
  blockedAt: string | null
  bookingCount: number
  valueTotal: string
  createdAt: string
  createdAtDisplay: string | null
  identities: Array<{
    id: string
    kind: string
    identifier: string | null
    blockedAt: string | null
    threadCount: number
  }>
  assignedUser: { id: string; name: string } | null
  entityLabel: string
  valueLabel: string
  currency: string
  /** ⛔ The contact's own notes, which lead the record. Amir 2026-09-12. */
  notes: RecordNote[]
  bookings: Array<{
    id: string
    reference: number
    title: string | null
    stageLabel: string
    stageKind: string
    stageColour: string | null
    pipelineLabel: string
    value: string | null
    closedAt: string | null
    dates: Array<{ label: string; value: string }>
    /** ⛔ This booking's own, under its own card. Contact notes are not repeated. */
    notes: RecordNote[]
  }>
  bookingTotal: number
  threads: Array<{
    id: string
    channel: string
    status: string
    identifier: string | null
    ourNumber: string
    lastMessageAt: string | null
    lastMessageAtDisplay: string | null
    preview: string | null
    previewDirection: string | null
  }>
  tags: Array<{ id: string; label: string; colour: string | null }>
  tasks: Array<{
    id: string
    title: string
    dueAt: string
    dueAtDisplay: string | null
    completedAt: string | null
  }>
  fields: RecordField[]
  /** ⛔ How many decimal places money shows. `org_settings`, zero here. */
  decimalPlaces: number
}

/**
 * ⚠️ THE SERVER'S OWN TYPE AGAIN, and the import is `import type` on purpose:
 * `list-columns.ts` imports the database driver, and a VALUE import from it
 * would take Postgres into the browser bundle. That is not hypothetical - it
 * happened on 2026-09-12 with `ROWS_PER_PAGE` and cost 127 modules. A type
 * import is erased and creates no module graph.
 */
export type { ResolvedColumn } from '../../../src/services/list-columns.js'
import type { ResolvedColumn } from '../../../src/services/list-columns.js'

export type ContactListRow = {
  id: string
  reference: number
  displayName: string | null
  firstName: string | null
  lastName: string | null
  country: string | null
  email: string | null
  language: string | null
  /** ⛔ The visible custom columns' values, keyed by column id, already rendered
   *  to a string by the server. The client has no field-type table. */
  customValues: Record<string, string | null>
  /** ⛔ The name, resolved by the server. The code is kept because a filter and
   * an export both want it, and the two must not drift apart on the client. */
  countryName: string | null
  phone: string | null
  bookingCount: number
  valueTotal: string
  blockedAt: string | null
  assignedName: string | null
  createdAt: string
  /**
   * ⛔ RENDERED BY THE SERVER, in the account's date format and the viewer's
   * timezone. The client sliced ten characters off the ISO string until
   * 2026-09-12, which is UTC and therefore a day out for an account east of it.
   * The ISO stays because an export and an ordering want it.
   */
  createdAtDisplay: string | null
  blockedAtDisplay: string | null
}

export type ContactsPageResponse = {
  rows: ContactListRow[]
  /** ⛔ 1-based, echoed back. `pageCount` is a floor when `totalIsCapped`. */
  page: number
  pageCount: number
  /** ⛔ WHY the list is empty, decided by the server. Three absences the client
   * cannot distinguish on its own - it cannot see the scope at all. */
  emptiness: 'none' | 'no-contacts-at-all' | 'no-matches' | 'none-visible'
  blockedHidden: number
  /** ⛔ Both configured in `org_settings` and neither was read until a screenshot
   * showed a column headed "Value" over a bare `3900.00`. */
  valueLabel: string
  currency: string
  /** ⛔ How many decimal places money shows. `org_settings`, zero here. */
  decimalPlaces: number
  /** ⛔ Amir's ruling B: every result set states its total. `null` means "not
   * recounted on this page", never "none". */
  total: number | null
  /** True when the count hit its cap, so `total` is a floor. */
  totalIsCapped: boolean
  /** ⛔ What the country filter did: absent, applied, or asked for a code
   * nothing recognises. The third is why this is not a boolean. */
  appliedCountry: { code: string; name: string | null } | null
  /** ⛔ Which columns to render and in what order, resolved by the server. It
   *  carries the hidden ones too, or the chooser could not turn one back on. */
  columns: ResolvedColumn[]
}

export async function fetchContactsPage(params: {
  sort?: string
  dir?: string
  q?: string
  country?: string
  view?: string
  /** ⛔ Numbered pages replaced the cursor, Amir 2026-09-12. One mechanism. */
  page?: number
  rows?: number
}): Promise<ContactsPageResponse> {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    /** ⚠️ `String(value)` since `page` and `rows` are numbers: the old loop took
     *  only strings and would have coerced them anyway, silently. */
    if (value !== undefined && value !== '') search.set(key, String(value))
  }
  const res = await fetch(`/api/contacts?${search.toString()}`, { credentials: 'same-origin' })
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('unauthorized')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as ContactsPageResponse
}

/**
 * ⛔ THE WHOLE COLUMN SET IN ONE REQUEST, IN ORDER. Reordering is a property of
 * the set: "move Country above Bookings" says nothing on its own about what
 * happened to Bookings, and a request per column lets a dropped connection leave
 * two columns claiming one position.
 */
export async function saveListColumns(
  list: string,
  columns: Array<{ id: string; visible: boolean }>,
): Promise<{ columns: ResolvedColumn[] }> {
  const res = await fetch(`/api/lists/${encodeURIComponent(list)}/columns`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ columns }),
  })
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('unauthorized')
  }
  if (res.status === 422) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error ?? 'that column set was not accepted')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as { columns: ResolvedColumn[] }
}

/**
 * ⛔ SAVED SEARCHES. The server hands back a query STRING, not a filter object:
 * restoring one means putting those parameters in the URL and letting the list
 * filter as it always does, so there is no second filtering path on this side.
 */
export type SavedSearch = {
  id: string
  label: string
  params: string
  /** Whether this viewer may delete it. Everyone can see one; only its author
   *  can remove it. */
  mine: boolean
  createdByName: string | null
}

export async function fetchSavedSearches(list: string): Promise<SavedSearch[]> {
  const res = await fetch(`/api/lists/${encodeURIComponent(list)}/searches`, {
    credentials: 'same-origin',
  })
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('unauthorized')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return ((await res.json()) as { searches: SavedSearch[] }).searches
}

export async function createSavedSearch(
  list: string,
  label: string,
  params: string,
): Promise<SavedSearch> {
  const res = await fetch(`/api/lists/${encodeURIComponent(list)}/searches`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ label, params }),
  })
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('unauthorized')
  }
  if (res.status === 422) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error ?? 'that search could not be saved')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as SavedSearch
}

export async function deleteSavedSearch(list: string, id: string): Promise<void> {
  const res = await fetch(
    `/api/lists/${encodeURIComponent(list)}/searches/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      /**
       * ⛔ NO `content-type` HEADER ON A BODYLESS DELETE. Fastify answers
       * `application/json` with no body as a 400 BEFORE `preHandler`, so the
       * route's authentication never runs - which is exactly how note delete
       * shipped broken and stayed broken. Measured: 400 with the header, 401
       * without it.
       */
      credentials: 'same-origin',
    },
  )
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('unauthorized')
  }
  if (res.status === 403) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error ?? 'that is not yours to delete')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
}

/**
 * ⛔ BLOCK AND UNBLOCK. CRM-BUILD item 14, and the first writer of `blocked_at`
 * the product has ever had - the column shipped in `0024` and four surfaces read
 * it while only the seeder wrote one.
 *
 * ⚠️ `blocked` IS EXPLICIT, NEVER A TOGGLE. A retried toggle does the opposite of
 * the first attempt, which is the one thing a network retry must not do.
 */
export async function setContactBlocked(reference: number, blocked: boolean): Promise<void> {
  const res = await fetch(`/api/contacts/${encodeURIComponent(reference)}/blocked`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ blocked }),
  })
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('unauthorized')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
}

export async function fetchContactRecord(reference: number): Promise<ContactRecordResponse> {
  const res = await fetch(`/api/contacts/${encodeURIComponent(reference)}`, {
    credentials: 'same-origin',
  })
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('unauthorized')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as ContactRecordResponse
}

/**
 * ⛔ A 422 CARRIES A MESSAGE A PERSON CAN ACT ON, and it is thrown with that
 * message rather than a status code. "abc" typed into a numeric field is the
 * person's mistake; the field row shows what the database said about it.
 */
export async function setContactField(
  reference: number,
  fieldId: string,
  value: string | null,
): Promise<void> {
  const res = await fetch(
    `/api/contacts/${encodeURIComponent(reference)}/fields/${encodeURIComponent(fieldId)}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ value }),
    },
  )
  if (res.status === 401) {
    reportUnauthorized()
    throw new Error('unauthorized')
  }
  if (res.status === 422) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error ?? 'that value was not accepted')
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
}
